import  { useEffect, useState } from 'react';
import { Plus, ArrowDownLeft, CreditCard, CheckCircle, Loader2, X, Smartphone, Wallet, Check } from 'lucide-react';
import { useApp } from "@/contexts/AppContext";
import { Helmet } from "react-helmet-async";
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import {
  getTransaction,
  AddFund,
  getBalance,
  AddWithdraw,
  initializeCryptoTopup,
  verifyCryptoTopup,
  buyKxtWithBalance,
  buyKxtWithCrypto,
  type WalletCryptoChain,
  type WalletCryptoSymbol,
  type WalletCryptoTopupInitResponse
} from '../../api/services/walletService'
import { initializeFlutterwave } from '@/api/services/flutterwaveService';
import { KXT_REDEMPTION_ENABLED } from '@/lib/featureFlags';
import DonationCryptoPaymentModal from '@/components/DonationCryptoPaymentModal';
import FeedbackModal from '@/components/FeedbackModal';
import { apiClient } from '@/api/client';
import ENDPOINTS from '@/api/endpoints';
import { getErrorMessage } from '@/utils/errorUtils';

type CryptoPaymentDetails = {
  crypto: WalletCryptoSymbol;
  chain: WalletCryptoChain;
  baseFiat: string;
};

type CryptoPaymentInitData = WalletCryptoTopupInitResponse;
type TransactionTabKey = 'all' | 'subscriptions' | 'top-up';
type WalletTransactionsPagination = {
  page: number;
  limit: number;
  totalPages: number;
  hasMore: boolean;
};

const DEFAULT_CRYPTO_DETAILS: CryptoPaymentDetails = {
  crypto: 'USDT',
  chain: 'BSC_TESTNET',
  baseFiat: 'USD'
};

const DEFAULT_TRANSACTION_LIMIT = 10;

interface WalletBalanceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onProceed: () => void;
  selectedSource: 'flat' | 'celo';
  onSelectSource: (source: 'flat' | 'celo') => void;
  balances: Record<string, unknown>;
  amountUsd: number | string;
  isProcessing: boolean;
  t: TFunction;
}

function WalletBalanceModal({
  isOpen,
  onClose,
  onProceed,
  selectedSource,
  onSelectSource,
  balances,
  amountUsd,
  isProcessing,
  t
}: WalletBalanceModalProps) {
  if (!isOpen) return null;

  const options: Array<{
    id: 'flat' | 'celo';
    title: string;
    amount: number | string | undefined;
    approx?: number | string | undefined;
    helper: string;
  }> = [
    {
      id: 'flat',
      title: 'Fiat Balance',
      amount: String((balances as unknown as Record<string, unknown>)?.fiatBalance || 0),
      helper: 'Use your fiat wallet balance'
    },
    {
      id: 'celo',
      title: 'Celo Balance',
      amount: String((balances as unknown as Record<string, unknown>)?.celoBalance || 0),
      approx: String((balances as unknown as Record<string, unknown>)?.celoBalanceUSD || 0),
      helper: 'Use your Celo wallet balance'
    }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-[#2a2b2e] rounded-2xl shadow-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700 sticky top-0 bg-white dark:bg-[#2a2b2e] z-10">
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
              {(t as unknown as (...args: unknown[]) => string)('enduser.plan.payment.walletSourceTitle', { defaultValue: 'Choose Wallet Balance' })}
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Select which balance to use to buy ${amountUsd} worth of KXT.
            </p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">
            <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        <div className="p-4 sm:p-6 space-y-3">
          {options.map((option) => {
            const isSelected = selectedSource === option.id;
            return (
              <button
                key={option.id}
                onClick={() => onSelectSource(option.id)}
                className={`w-full p-3 sm:p-4 rounded-xl border-2 transition-all text-left ${
                  isSelected
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="font-semibold text-sm sm:text-base text-gray-900 dark:text-white">
                      {option.title}
                    </h3>
                    <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-1">
                      {option.helper}
                    </p>
                  </div>
                  {isSelected && (
                    <div className="w-5 h-5 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0">
                      <Check className="w-3 h-3 text-white" />
                    </div>
                  )}
                </div>
                <div className="mt-3">
                  <p className="text-lg font-bold text-gray-900 dark:text-white">
                    {option.amount ?? '0'}
                  </p>
                  {option.approx !== undefined && (
                    <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-1">
                      Approx. ${option.approx} USD
                    </p>
                  )}
                </div>
              </button>
            );
          })}
        </div>

        <div className="flex justify-between p-4 sm:p-6 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            disabled={isProcessing}
            className="px-6 py-2.5 text-gray-700 dark:text-gray-300 font-medium hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onProceed}
            disabled={isProcessing}
            className="px-8 py-2.5 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
          >
            {isProcessing ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>{t('enduser.plan.payment.processing', { defaultValue: 'Processing...' })}</span>
              </>
            ) : (
              <span>{t('enduser.plan.actions.proceed', { defaultValue: 'Proceed to Payment' })}</span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function WalletDashboard() {
     const { theme } = useApp();
  const { i18n } = useTranslation();
  const [langOverride, setLangOverride] = useState(
    localStorage.getItem('i18nextLng')?.startsWith('fr') ? 'fr' : 'en'
  );
  const t = i18n.getFixedT(langOverride);
  const [activeTab, setActiveTab] = useState<TransactionTabKey>('all');
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [showSuccess, setShowSuccess] = useState(false);
  const [showFlutterwaveModal, setShowFlutterwaveModal] = useState(false);
  const [showFlutterwaveIframe, setShowFlutterwaveIframe] = useState(false);
  const [showCryptoFundModal, setShowCryptoFundModal] = useState(false);
  const [showCryptoPaymentModal, setShowCryptoPaymentModal] = useState(false);
  const [showKxtPaymentMethodModal, setShowKxtPaymentMethodModal] = useState(false);
  const [showKxtWalletBalanceModal, setShowKxtWalletBalanceModal] = useState(false);
  const [selectedKxtWalletBalance, setSelectedKxtWalletBalance] = useState<'flat' | 'celo'>('flat');
  const [flutterwavePaymentLink, setFlutterwavePaymentLink] = useState<string | null>(null);
  const [cryptoDetails, setCryptoDetails] = useState<CryptoPaymentDetails>(DEFAULT_CRYPTO_DETAILS);
  const [cryptoPaymentData, setCryptoPaymentData] = useState<CryptoPaymentInitData | null>(null);
  const [isInitializingCryptoTopup, setIsInitializingCryptoTopup] = useState(false);
  const [isVerifyingCryptoTopup, setIsVerifyingCryptoTopup] = useState(false);
  const [isLoadingTransactions, setIsLoadingTransactions] = useState(false);
  const [isLoadingMoreTransactions, setIsLoadingMoreTransactions] = useState(false);

  const [transactions, setTransaction] = useState<unknown[]>([]);
  const [transactionsPagination, setTransactionsPagination] = useState<WalletTransactionsPagination>({
    page: 1,
    limit: DEFAULT_TRANSACTION_LIMIT,
    totalPages: 1,
    hasMore: false,
  });
  const [balance, setBalance] = useState(null);
  const [fundfield, setfundfield] = useState<Record<string, unknown>>({
    amount:"",
    currency: "USD", 
    paymentMethod: "card",
    paymentMethodId: ""
    });
  const [userInfo, setUserInfo] = useState({
    email: '',
    phone: '',
    fullName: ''
  });
  const [withdrawfield, setwithdrawfield] = useState<Record<string, unknown>>({
    amount:"",
    currency: "USD", 
    withdrawMethod: "crypto",
    bankAccountNumber: "",
    cryptoWalletAddress: "",
    bankname: "",
    accountname: "",
    mobileNumber: "",
    mobileNetwork: "",
    });
  const [kxtAmount, setKxtAmount] = useState("");
  const [isBuyingKxt, setIsBuyingKxt] = useState(false);
  const [flutterwaveMode, setFlutterwaveMode] = useState<'fund' | 'kxt'>('fund');
  const [error, setError] = useState("");
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackData, setFeedbackData] = useState<{
    type: 'success' | 'error' | 'warning' | 'info';
    title: string;
    message: string;
  }>({ type: 'success', title: '', message: '' });

  const refreshWalletData = async (tab: TransactionTabKey = activeTab) => {
    const response = await getBalance();
    setBalance((response as unknown as Record<string, unknown>).data as unknown as Record<string, unknown>);
    await fetchTransactions(tab, 1, DEFAULT_TRANSACTION_LIMIT, false);
  };

  const transactionTabs: Array<{ key: TransactionTabKey; label: string }> = [
    { key: 'all', label: t('enduser.wallet.transactions.tabs.0', { defaultValue: 'All' }) },
    { key: 'subscriptions', label: t('enduser.wallet.transactions.tabs.1', { defaultValue: 'Subscriptions' }) },
    { key: 'top-up', label: t('enduser.wallet.transactions.tabs.2', { defaultValue: 'Top-ups' }) },
  ];

  // Real transaction status drives the badge + dot. Previously every row was
  // hardcoded to 'completed', so failed top-ups looked successful.
  const txStatusStyles = (status?: string) => {
    const st = String(status || '').toLowerCase();
    if (st === 'completed' || st === 'approved' || st === 'success' || st === 'successful') {
      return { badge: 'bg-green-600 text-white', dot: 'bg-green-500', dotBg: 'bg-green-100' };
    }
    if (st === 'failed' || st === 'cancelled' || st === 'canceled' || st === 'voided' || st === 'declined') {
      return { badge: 'bg-red-600 text-white', dot: 'bg-red-500', dotBg: 'bg-red-100' };
    }
    if (st === 'pending' || st === 'processing') {
      return { badge: 'bg-amber-500 text-white', dot: 'bg-amber-500', dotBg: 'bg-amber-100' };
    }
    return { badge: 'bg-gray-500 text-white', dot: 'bg-gray-400', dotBg: 'bg-gray-100' };
  };
  const txStatusLabel = (status?: string) => {
    const st = String(status || '').toLowerCase();
    if (!st) return 'unknown';
    return st.charAt(0).toUpperCase() + st.slice(1);
  };

  async function fetchTransactions(
    tab: TransactionTabKey,
    page = 1,
    limit = DEFAULT_TRANSACTION_LIMIT,
    append = false
  ){
    if (append) {
      setIsLoadingMoreTransactions(true);
    } else {
      setIsLoadingTransactions(true);
    }

    try {
      const response =
        tab === 'all'
          ? await getTransaction(undefined, page, limit)
          : await getTransaction(tab, page, limit);

      const items = (((response as unknown as Record<string, unknown>).data as Record<string, unknown>)?.transactions || (response as unknown as Record<string, unknown>).transactions || []) as unknown[];
      const paginationPayload = (((response as unknown as Record<string, unknown>).data as Record<string, unknown>)?.pagination || (response as unknown as Record<string, unknown>).pagination || {}) as Record<string, unknown>;
      const nextPage = (paginationPayload.page as number) || (paginationPayload.currentPage as number) || page;
      const nextLimit = (paginationPayload.limit as number) || (paginationPayload.itemsPerPage as number) || limit;
      const totalPages =
        (paginationPayload.totalPages as number) ||
        ((paginationPayload.total as number) && nextLimit
          ? Math.ceil((paginationPayload.total as number) / nextLimit)
          : nextPage);
      const hasMore =
        (paginationPayload.hasMore as boolean) ??
        (paginationPayload.hasNext as boolean) ??
        nextPage < totalPages;

      setTransaction((prev) => (append ? [...prev, ...items] : items));
      setTransactionsPagination({
        page: nextPage,
        limit: nextLimit,
        totalPages,
        hasMore,
      });
    } catch (err: unknown) {
      setError(getErrorMessage(err));
      setTimeout(() => setError(""), 3000);
    } finally {
      if (append) {
        setIsLoadingMoreTransactions(false);
      } else {
        setIsLoadingTransactions(false);
      }
    }
  }

  const loadMoreTransactions = () => {
    if (!transactionsPagination.hasMore || isLoadingMoreTransactions) {
      return;
    }

    fetchTransactions(
      activeTab,
      transactionsPagination.page + 1,
      transactionsPagination.limit,
      true
    );
  };

    async function addWithdraw(){
      try{
      const parsedAmount = Number((withdrawfield as unknown as Record<string, unknown>).amount);
      if (!(withdrawfield as unknown as Record<string, unknown>).amount || !Number.isFinite(parsedAmount) || parsedAmount <= 0) {
        setError("Please enter a valid amount.");
        setTimeout(() => setError(""), 3000);
        return;
      }

      const isBankTransfer = (withdrawfield as unknown as Record<string, unknown>).withdrawMethod === "bank_transfer";
      const isMobileMoney = (withdrawfield as unknown as Record<string, unknown>).withdrawMethod === "mobile_money";
      const withdrawTo = isBankTransfer
        ? ((withdrawfield as unknown as Record<string, unknown>).bankAccountNumber as string)?.trim()
        : isMobileMoney
        ? ((withdrawfield as unknown as Record<string, unknown>).mobileNumber as string)?.trim()
        : ((withdrawfield as unknown as Record<string, unknown>).cryptoWalletAddress as string)?.trim();

      if (!withdrawTo) {
        setError(isBankTransfer ? "Please enter an account number." : isMobileMoney ? "Please enter a mobile number." : "Please enter a wallet address.");
        setTimeout(() => setError(""), 3000);
        return;
      }

      if (isBankTransfer && (!((withdrawfield as unknown as Record<string, unknown>).bankname as string)?.trim() || !((withdrawfield as unknown as Record<string, unknown>).accountname as string)?.trim())) {
        setError("Please enter your bank name and account name.");
        setTimeout(() => setError(""), 3000);
        return;
      }

      if (isMobileMoney && (!((withdrawfield as unknown as Record<string, unknown>).mobileNetwork as string)?.trim() || !((withdrawfield as unknown as Record<string, unknown>).accountname as string)?.trim())) {
        setError("Please enter your mobile network and account name.");
        setTimeout(() => setError(""), 3000);
        return;
      }

      const payload = {
        amount: parsedAmount,
        currency: (withdrawfield as unknown as Record<string, unknown>).currency,
        withdrawTo,
        withdrawMethod: (withdrawfield as unknown as Record<string, unknown>).withdrawMethod,
        ...(isBankTransfer
          ? {
              bankname: ((withdrawfield as unknown as Record<string, unknown>).bankname as string).trim(),
              accountname: ((withdrawfield as unknown as Record<string, unknown>).accountname as string).trim(),
            }
          : isMobileMoney
          ? {
              network: ((withdrawfield as unknown as Record<string, unknown>).mobileNetwork as string).trim(),
              accountname: ((withdrawfield as unknown as Record<string, unknown>).accountname as string).trim(),
            }
          : {}),
      };

      const response = await AddWithdraw(payload);
            console.log('Fetched added fund:', response);
    setwithdrawfield((prev: Record<string, unknown>) => ({
      ...prev,
      amount: "",
      bankAccountNumber: "",
      cryptoWalletAddress: "",
      bankname: "",
      accountname: "",
      mobileNumber: "",
      mobileNetwork: "",
    }));
    setShowSuccess(true);
    setTimeout(() => setShowSuccess(false), 3000);
      } catch (err: unknown){
    setError(getErrorMessage(err));
    setTimeout(() => setError(""), 3000);

      }
    }
    async function addFund(){
      try{
        const parsedAmount = Number((fundfield as unknown as Record<string, unknown>).amount);
        if (!(fundfield as unknown as Record<string, unknown>).amount || !Number.isFinite(parsedAmount) || parsedAmount <= 0) {
          setError("Please enter a valid amount.");
          setTimeout(() => setError(""), 3000);
          return;
        }
        if(paymentMethod === "card"){
          setFlutterwaveMode('fund');
          setShowFlutterwaveModal(true);
          return;
        }
        if (paymentMethod === "mobile_money") {
          setFlutterwaveMode('fund');
          setShowFlutterwaveModal(true);
          return;
        }
        if (paymentMethod === "crypto") {
          setShowCryptoFundModal(true);
          return;
        }
        // If neither card nor crypto is selected, fail gracefully.
        // Previously fell through to a mock random-wallet-address path,
        // which submitted invalid data to the backend.
        setError("Please select a valid payment method (Card or Crypto).");
        setTimeout(() => setError(""), 3000);
        return;
      } catch (err: unknown){
    setError(getErrorMessage(err));
    setTimeout(() => setError(""), 3000);
      }
    }
  // Refresh wallet data on initial load and when the user switches tabs.
  // We deliberately do NOT depend on showSuccess here — that caused a double
  // fetch (once when the success toast opened, again when it auto-closed).
  // Use the 'walletBalanceRefresh' event for explicit refresh on payment success.
  useEffect(() => {
    refreshWalletData(activeTab).catch((err: unknown) => {
      setError(getErrorMessage(err) || String(err));
      setTimeout(() => setError(""), 3000);
    });
  }, [activeTab]);

  // When a payment succeeds, refresh the wallet data once.
  useEffect(() => {
    if (!showSuccess) return;
    refreshWalletData(activeTab).catch((err: unknown) => {
      setError(getErrorMessage(err) || String(err));
      setTimeout(() => setError(""), 3000);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSuccess]);

  useEffect(() => {
    const handleWalletRefresh = () => {
      refreshWalletData(activeTab).catch((err: unknown) => {
        setError(getErrorMessage(err) || String(err));
        setTimeout(() => setError(""), 3000);
      });
    };

    window.addEventListener("walletBalanceRefresh", handleWalletRefresh);

    return () => {
      window.removeEventListener("walletBalanceRefresh", handleWalletRefresh);
    };
  }, [activeTab]);

  useEffect(() => {
    const storedLang = localStorage.getItem('i18nextLng');
    if (storedLang) {
      const normalized = storedLang.startsWith('fr') ? 'fr' : 'en';
      setLangOverride(normalized);
      if (i18n.language !== normalized) {
        i18n.changeLanguage(normalized);
      }
    }
    const onAppLanguageChanged = (event: Event) => {
      const next = (event as CustomEvent).detail as string;
      const normalized = next?.startsWith('fr') ? 'fr' : 'en';
      setLangOverride(normalized);
    };
    window.addEventListener('appLanguageChanged', onAppLanguageChanged as EventListener);
    return () => {
      window.removeEventListener('appLanguageChanged', onAppLanguageChanged as EventListener);
    };
  }, [i18n]);

  useEffect(() => {
    if (!showFlutterwaveIframe) return;
    const interval = setInterval(() => {
      const topupSuccess = localStorage.getItem('walletTopupSuccess') === 'true';
      if (topupSuccess) {
        localStorage.removeItem('walletTopupSuccess');
        setShowFlutterwaveIframe(false);
        setFlutterwavePaymentLink(null);
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 3000);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [showFlutterwaveIframe]);

  const handleFund = function(value, name){
    setfundfield((prev)=> ({...prev, [name]: value}))
  }
  const handleWithdraw = function(value, name){
    setwithdrawfield((prev)=> ({...prev, [name]: value}))
  }
  const handleCryptoDetailsChange = function(field: keyof CryptoPaymentDetails, value: string){
    setCryptoDetails((prev) => ({ ...prev, [field]: value as CryptoPaymentDetails[typeof field] }))
  }
  const closeCryptoFundModal = function(){
    setShowCryptoFundModal(false);
    setCryptoDetails(DEFAULT_CRYPTO_DETAILS);
  }
  const closeCryptoPaymentModal = function(){
    setShowCryptoPaymentModal(false);
    setCryptoPaymentData(null);
    setCryptoDetails(DEFAULT_CRYPTO_DETAILS);
  }
  const handleCryptoPaymentVerify = async function(){
    try {
      const reference = cryptoPaymentData?.reference || cryptoPaymentData?.transactionId;
      if (!reference) {
        setFeedbackData({
          type: 'error',
          title: 'Verification Failed',
          message: 'Missing payment reference.'
        });
        setShowFeedback(true);
        return;
      }
      setIsVerifyingCryptoTopup(true);
      const response = await verifyCryptoTopup(reference);
      const success = response?.success ?? response?.data?.success;
      if (!success) {
        throw new Error(response?.message || response?.data?.message || 'Payment verification failed');
      }
      closeCryptoPaymentModal();
      setfundfield((prev) => ({ ...prev, amount: "" }));
      setFeedbackData({
        type: 'success',
        title: 'Payment Verified',
        message: response?.message || response?.data?.message || 'Payment verified successfully.'
      });
      setShowFeedback(true);
    } catch (err: unknown) {
      const errorObj = err as { response?: { data?: { error?: { message?: string }, message?: string } }, message?: string };
      const errorMessage =
        errorObj?.response?.data?.error?.message ||
        errorObj?.response?.data?.message ||
        errorObj?.message ||
        'Payment verification failed.';
      setFeedbackData({
        type: 'error',
        title: 'Verification Failed',
        message: errorMessage
      });
      setShowFeedback(true);
    } finally {
      setIsVerifyingCryptoTopup(false);
    }
  }
  const proceedCryptoTopup = async function(){
    try {
      const sourceAmount = flutterwaveMode === 'kxt' ? kxtAmount : (fundfield as unknown as Record<string, unknown>).amount;
      const parsedAmount = Number(sourceAmount);
      if (!Number.isFinite(parsedAmount) || parsedAmount <= 0) {
        setError("Please enter a valid amount.");
        setTimeout(() => setError(""), 3000);
        return;
      }
      setIsInitializingCryptoTopup(true);
      const response = await initializeCryptoTopup({
        amount: parsedAmount,
        crypto: cryptoDetails.crypto,
        chain: cryptoDetails.chain,
        baseFiat: cryptoDetails.baseFiat
      });
      const paymentData = ((response as unknown as Record<string, unknown>)?.data ?? response) as CryptoPaymentInitData | null;
      if (!paymentData?.address) {
        throw new Error(String((response as unknown as Record<string, unknown>)?.message || 'Failed to initialize crypto topup'));
      }
      setShowCryptoFundModal(false);
      setCryptoPaymentData(paymentData);
      setShowCryptoPaymentModal(true);
    } catch (err: unknown) {
      setError(getErrorMessage(err));
      setTimeout(() => setError(""), 3000);
    } finally {
      setIsInitializingCryptoTopup(false);
    }
  }
  return (
     <>
      <Helmet>
        <title>{t('enduser.wallet.metaTitle', { defaultValue: 'Wallet Dashboard | KonectX' })}</title>
        <meta
          name="description"
          content={t('enduser.wallet.metaDescription', { defaultValue: 'Manage your KonectX wallet. View your $KXT token balance, transaction history, and send or receive tokens within the decentralized network.' })}
        />
        <meta
          name="keywords"
          content="KonectX, wallet dashboard, KXT token, transactions, send receive tokens, decentralized network"
        />
        <meta property="og:title" content={t('enduser.wallet.metaTitle', { defaultValue: 'Wallet Dashboard | KonectX' })} />
        <meta
          property="og:description"
          content={t('enduser.wallet.metaDescription', { defaultValue: 'Manage your KonectX wallet. View your $KXT token balance, transaction history, and send or receive tokens within the decentralized network.' })}
        />
      </Helmet>

    <div className={`min-h-screen ${theme === 'dark' ? 'bg-black' : 'bg-gray-50'}`}>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Title Section */}
        <div className="mb-8">
          <h2 className={`text-3xl font-bold mb-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
            {t('enduser.wallet.headertitle', { defaultValue: 'Wallet' })}
          </h2>
          <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
            {t('enduser.wallet.headerbody1', { defaultValue: 'Manage your balance and transactions' })}
          </p>
        </div>

      {/* Balance Cards */}
<div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
  {/* Celo Balance */}
  <div className={`rounded-2xl shadow-lg p-6 border-2 overflow-hidden min-w-0 ${theme === 'dark' ? 'bg-[#333436] border-gray-800' : 'bg-white border-gray-300'}`}>
    <div className="flex items-center justify-between mb-4">
      <span className={`text-xs font-bold tracking-wider ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
        {t('enduser.wallet.balances.0.title', { defaultValue: 'CELO BALANCE' })}
      </span>
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${theme === 'dark' ? 'bg-[#2a2b2d]' : 'bg-blue-50'}`}>
        <svg className="w-5 h-5" viewBox="0 0 32 32" aria-hidden="true">
          <circle cx="16" cy="16" r="15" fill={theme === 'dark' ? '#2563eb' : '#2563eb'} />
          <text x="16" y="16" textAnchor="middle" dominantBaseline="central" fontSize="14" fill="#ffffff" fontWeight="700">C</text>
        </svg>
      </div>
    </div>
    <div className={`text-xl sm:text-2xl md:text-3xl font-bold mb-2 truncate ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
      {balance?.celoBalance ?? "0"}
    </div>
    <div className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>
      ≈ ${balance?.celoBalanceUSD ?? "0"} USD
    </div>
  </div>

          {/* KXT Balance */}
          <div className={`rounded-2xl shadow-lg p-6 border-2 overflow-hidden min-w-0 ${theme === 'dark' ? 'bg-[#333436] border-gray-800' : 'bg-white border-gray-300'}`}>
            <div className="flex items-center justify-between mb-4">
      <span className={`text-xs font-bold tracking-wider ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
        {t('enduser.wallet.balances.1.title', { defaultValue: 'KXT BALANCE' })}
      </span>
            <div
  className={`w-8 h-8 rounded-lg flex items-center justify-center text-blue-600 text-xs font-bold
    ${theme === 'dark' ? 'bg-[#2a2b2d]' : 'bg-blue-50'}`}
>
  KXT
</div>

            </div>
            <div className={`text-xl sm:text-2xl md:text-3xl font-bold mb-2 truncate ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
              {balance?.kxtBalance ?? "0"}
            </div>
            <div className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>
              $KXT {t('enduser.wallet.balances.1.tokens', { defaultValue: 'Tokens' })}
            </div>
          </div>

  {/* Fiat Balance */}
  <div className={`rounded-2xl shadow-lg p-6 border-2 ${theme === 'dark' ? 'bg-[#333436] border-gray-800' : 'bg-white border-gray-300'}`}>
    <div className="flex items-center justify-between mb-4">
      <span className={`text-xs font-bold tracking-wider ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
        {t('enduser.wallet.balances.2.title', { defaultValue: 'FIAT BALANCE' })}
      </span>
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${theme === 'dark' ? 'bg-[#2a2b2d]' : 'bg-blue-50'}`}>
        <CreditCard className={`w-5 h-5 ${theme === 'dark' ? 'text-blue-600' : 'text-blue-600'}`} />
      </div>
    </div>
    <div className={`text-xl sm:text-2xl md:text-3xl font-bold mb-2 truncate ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
      {balance?.fiatBalance|| "0"}
    </div>
    <div className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>
      {t('enduser.wallet.balances.2.spend', { defaultValue: 'Available to spend' })}
    </div>
  </div>
</div>

     {/* Action Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Add Funds */}
          <div className={`rounded-xl shadow-md p-6 border ${theme === 'dark' ? 'bg-[#333436] border-gray-800' : 'bg-white border-gray-200'}`}>
            <div className="flex items-center gap-2.5 mb-2">
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${theme === 'dark' ? 'bg-blue-600/10' : 'bg-blue-50'}`}>
                <Plus className="w-5 h-5 text-blue-600" />
              </div>
              <h3 className={`text-base font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                {t('enduser.wallet.addfunds.title', { defaultValue: 'Add Funds' })}
              </h3>
            </div>
            <p className={`text-xs mb-4 ml-11 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
              {t('enduser.wallet.addfunds.description', { defaultValue: 'Top up your wallet balance' })}
            </p>
            
            <div className="mb-4">
              <label className={`block text-xs font-semibold mb-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                {t('enduser.wallet.addfunds.amountTitle', { defaultValue: 'Amount (USD)' })}
              </label>
              <input
                type="text"
                placeholder="50.00"
                value={String((fundfield as unknown as Record<string, unknown>).amount || "")}
                name='amount'
                onChange={(e)=> handleFund(e.target.value, e.target.name)}
                className={`w-full px-3.5 py-2.5 border rounded-lg text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${theme === 'dark' ? 'bg-[#2a2b2d] border-gray-700 text-white placeholder-gray-500' : 'bg-gray-50 border-gray-300 text-gray-900'}`}
              />
            </div>

            <div className="mb-4">
              <label className={`block text-xs font-semibold mb-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                {t('enduser.wallet.addfunds.paymentmethodTitle', { defaultValue: 'Payment Method' })}
              </label>
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                <button
                  onClick={() => {
                    setPaymentMethod('card')
                    handleFund('card', 'paymentMethod');
                  }}
                  className={`flex-1 px-3 py-2.5 rounded-lg border flex items-center justify-center gap-1.5 text-xs font-semibold transition-all ${
                    paymentMethod === 'card'
                      ? 'border-blue-600 bg-blue-600 text-white shadow-sm'
                      : theme === 'dark'
                      ? 'border-gray-700 bg-[#2a2b2d] text-gray-400 hover:border-gray-600'
                      : 'border-gray-300 bg-white text-gray-600 hover:border-gray-400'
                  }`}
                >
                  <CreditCard className="w-4 h-4" />
                  {t('enduser.wallet.addfunds.paymentoptions.0', { defaultValue: 'Card' })}
                </button>
                <button
                  onClick={() => {
                    setPaymentMethod('crypto')
                    handleFund('crypto', 'paymentMethod');
                  }}
                  className={`flex-1 px-3 py-2.5 rounded-lg border flex items-center justify-center gap-1.5 text-xs font-semibold transition-all ${
                    paymentMethod === 'crypto'
                      ? 'border-blue-600 bg-blue-600 text-white shadow-sm'
                      : theme === 'dark'
                      ? 'border-gray-700 bg-[#2a2b2d] text-gray-400 hover:border-gray-600'
                      : 'border-gray-300 bg-white text-gray-600 hover:border-gray-400'
                  }`}
                >
                  <svg className="w-4 h-4" viewBox="0 0 32 32" aria-hidden="true">
                    <circle cx="16" cy="16" r="15" fill="currentColor" />
                    <text x="16" y="16" textAnchor="middle" dominantBaseline="central" fontSize="12" fill="#ffffff" fontWeight="700">C</text>
                  </svg>
                  {t('enduser.wallet.addfunds.paymentoptions.1', { defaultValue: 'Crypto' })}
                </button>
                <button
                  onClick={() => {
                    setPaymentMethod('mobile_money')
                    handleFund('mobile_money', 'paymentMethod');
                  }}
                  className={`flex-1 px-3 py-2.5 rounded-lg border flex items-center justify-center gap-1.5 text-xs font-semibold transition-all ${
                    paymentMethod === 'mobile_money'
                      ? 'border-blue-600 bg-blue-600 text-white shadow-sm'
                      : theme === 'dark'
                      ? 'border-gray-700 bg-[#2a2b2d] text-gray-400 hover:border-gray-600'
                      : 'border-gray-300 bg-white text-gray-600 hover:border-gray-400'
                  }`}
                >
                  <Smartphone className="w-4 h-4" />
                  {t('enduser.wallet.addfunds.paymentoptions.2', { defaultValue: 'Mobile Money' })}
                </button>
              </div>
            </div>

            <button onClick={addFund} className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2.5 px-4 rounded-lg transition-colors text-sm shadow-sm">
              {t('enduser.wallet.addfunds.button', { defaultValue: 'Add Funds' })}
            </button>

            {/* Buy-KXT is forbidden for the beta (counsel, KX-LEG-KXT-001 Q1; KX-111
                item 9: "Buy-KXT and KXT redemption stay off"). The divider, amount
                input and Buy-KXT button below are gated OFF behind the flag. KXT is
                an accrued reward only — never bought, sold, redeemed or priced. */}
            {KXT_REDEMPTION_ENABLED && (
            <>
            {/* Or divider */}
            <div className="flex items-center gap-3 my-4">
              <div className={`flex-1 h-px ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-300'}`} />
              <span className={`text-xs font-semibold uppercase tracking-wider ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>
                {t('enduser.wallet.addfunds.or', { defaultValue: 'or' })}
              </span>
              <div className={`flex-1 h-px ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-300'}`} />
            </div>

            {/* Buy KXT Token */}
            <div className="mb-3">
              <label className={`block text-xs font-semibold mb-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                {t('enduser.wallet.addfunds.kxtAmountTitle', { defaultValue: 'Amount (USD)' })}
              </label>
              <input
                type="text"
                placeholder="100.00"
                value={kxtAmount}
                onChange={(e) => setKxtAmount(e.target.value)}
                className={`w-full px-3.5 py-2.5 border rounded-lg text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${theme === 'dark' ? 'bg-[#2a2b2d] border-gray-700 text-white placeholder-gray-500' : 'bg-gray-50 border-gray-300 text-gray-900'}`}
              />
            </div>

            <button
              onClick={() => {
                const parsed = Number(kxtAmount);
                if (!kxtAmount || !Number.isFinite(parsed) || parsed <= 0) {
                  setError(t('enduser.wallet.messages.invalidAmount', { defaultValue: 'Please enter a valid amount.' }));
                  setTimeout(() => setError(""), 3000);
                  return;
                }
                setShowKxtPaymentMethodModal(true);
              }}
              className={`w-full font-semibold py-2.5 px-4 rounded-lg transition-colors text-sm shadow-sm flex items-center justify-center gap-2 ${
                theme === 'dark'
                  ? 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white'
                  : 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white'
              }`}
            >
              {t('enduser.wallet.addfunds.buyKxt', { defaultValue: 'Buy KXT Token' })}
            </button>
            </>
            )}
          </div>

          {/* Withdraw - Simplified Design */}
          <div className={`rounded-xl shadow-md p-6 border ${theme === 'dark' ? 'bg-[#333436] border-gray-800' : 'bg-white border-gray-200'}`}>
            <div className="flex items-center gap-2.5 mb-2">
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${theme === 'dark' ? 'bg-blue-600/10' : 'bg-blue-50'}`}>
                <ArrowDownLeft className="w-5 h-5 text-blue-600" />
              </div>
              <h3 className={`text-lg font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                {t('enduser.wallet.withdrawfunds.title', { defaultValue: 'Withdraw' })}
              </h3>
            </div>
            <p className={`text-sm mb-6 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
              {t('enduser.wallet.withdrawfunds.description', { defaultValue: 'Transfer funds to your account' })}
            </p>
            
            <div className="mb-4">
              <label className={`block text-xs font-semibold mb-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                {t('enduser.wallet.withdrawfunds.amountTitle', { defaultValue: 'Amount (USD)' })}
              </label>
              <input
                type="text"
                onChange={(e)=> handleWithdraw(e.target.value, e.target.name)}
                name='amount'
                value={String((withdrawfield as unknown as Record<string, unknown>).amount || "")}
                placeholder="25.00"
                className={`w-full px-3.5 py-2.5 border rounded-lg text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${theme === 'dark' ? 'bg-[#2a2b2d] border-gray-700 text-white placeholder-gray-500' : 'bg-gray-50 border-gray-300 text-gray-900'}`}
              />
            </div>

            <div className="mb-4">
              <label className={`block text-xs font-semibold mb-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                {t('enduser.wallet.withdrawfunds.methodTitle', { defaultValue: 'Withdrawal Method' })}
              </label>
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                <button
                  onClick={() => handleWithdraw('crypto', 'withdrawMethod')}
                  className={`flex-1 px-3 py-2.5 rounded-lg border flex items-center justify-center gap-1.5 text-xs font-semibold transition-all ${
                    (withdrawfield as unknown as Record<string, unknown>).withdrawMethod === 'crypto'
                      ? 'border-blue-600 bg-blue-600 text-white shadow-sm'
                      : theme === 'dark'
                      ? 'border-gray-700 bg-[#2a2b2d] text-gray-400 hover:border-gray-600'
                      : 'border-gray-300 bg-white text-gray-600 hover:border-gray-400'
                  }`}
                >
                  <svg className="w-4 h-4" viewBox="0 0 32 32" aria-hidden="true">
                    <circle cx="16" cy="16" r="15" fill="currentColor" />
                    <text x="16" y="16" textAnchor="middle" dominantBaseline="central" fontSize="12" fill="#ffffff" fontWeight="700">C</text>
                  </svg>
                  {t('enduser.wallet.withdrawfunds.methods.crypto', { defaultValue: 'Crypto' })}
                </button>
                <button
                  onClick={() => handleWithdraw('bank_transfer', 'withdrawMethod')}
                  className={`flex-1 px-3 py-2.5 rounded-lg border flex items-center justify-center gap-1.5 text-xs font-semibold transition-all ${
                    (withdrawfield as unknown as Record<string, unknown>).withdrawMethod === 'bank_transfer'
                      ? 'border-blue-600 bg-blue-600 text-white shadow-sm'
                      : theme === 'dark'
                      ? 'border-gray-700 bg-[#2a2b2d] text-gray-400 hover:border-gray-600'
                      : 'border-gray-300 bg-white text-gray-600 hover:border-gray-400'
                  }`}
                >
                  <CreditCard className="w-4 h-4" />
                  {t('enduser.wallet.withdrawfunds.methods.bank', { defaultValue: 'Bank' })}
                </button>
                <button
                  onClick={() => handleWithdraw('mobile_money', 'withdrawMethod')}
                  className={`flex-1 px-3 py-2.5 rounded-lg border flex items-center justify-center gap-1.5 text-xs font-semibold transition-all ${
                    (withdrawfield as unknown as Record<string, unknown>).withdrawMethod === 'mobile_money'
                      ? 'border-blue-600 bg-blue-600 text-white shadow-sm'
                      : theme === 'dark'
                      ? 'border-gray-700 bg-[#2a2b2d] text-gray-400 hover:border-gray-600'
                      : 'border-gray-300 bg-white text-gray-600 hover:border-gray-400'
                  }`}
                >
                  <CreditCard className="w-4 h-4" />
                  {t('enduser.wallet.withdrawfunds.methods.mobileMoney', { defaultValue: 'Mobile Money' })}
                </button>
              </div>
            </div>

            {(withdrawfield as unknown as Record<string, unknown>).withdrawMethod === 'bank_transfer' ? (
              <>
                <div className="mb-4">
                  <label className={`block text-sm font-semibold mb-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                    {t('enduser.wallet.withdrawfunds.bankNameTitle', { defaultValue: 'Bank Name' })}
                  </label>
                  <input
                    type="text"
                    onChange={(e)=> handleWithdraw(e.target.value, e.target.name)}
                    name='bankname'
                    value={String((withdrawfield as unknown as Record<string, unknown>).bankname || "")}
                    placeholder={t('enduser.wallet.withdrawfunds.bankNamePlaceholder', { defaultValue: 'Enter bank name' })}
                    className={`w-full px-3.5 py-2.5 border rounded-lg text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${theme === 'dark' ? 'bg-[#2a2b2d] border-gray-700 text-white placeholder-gray-500' : 'bg-gray-50 border-gray-300 text-gray-900'}`}
                  />
                </div>

                <div className="mb-4">
                  <label className={`block text-sm font-semibold mb-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                    {t('enduser.wallet.withdrawfunds.accountNumberTitle', { defaultValue: 'Account Number' })}
                  </label>
                  <input
                    type="text"
                    onChange={(e)=> handleWithdraw(e.target.value, e.target.name)}
                    name='bankAccountNumber'
                    value={String((withdrawfield as unknown as Record<string, unknown>).bankAccountNumber || "")}
                    placeholder={t('enduser.wallet.withdrawfunds.accountNumberPlaceholder', { defaultValue: 'Enter account number' })}
                    className={`w-full px-3.5 py-2.5 border rounded-lg text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${theme === 'dark' ? 'bg-[#2a2b2d] border-gray-700 text-white placeholder-gray-500' : 'bg-gray-50 border-gray-300 text-gray-900'}`}
                  />
                </div>

                <div className="mb-6">
                  <label className={`block text-sm font-semibold mb-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                    {t('enduser.wallet.withdrawfunds.accountNameTitle', { defaultValue: 'Account Name' })}
                  </label>
                  <input
                    type="text"
                    onChange={(e)=> handleWithdraw(e.target.value, e.target.name)}
                    name='accountname'
                    value={String((withdrawfield as unknown as Record<string, unknown>).accountname || "")}
                    placeholder={t('enduser.wallet.withdrawfunds.accountNamePlaceholder', { defaultValue: 'Enter account name' })}
                    className={`w-full px-3.5 py-2.5 border rounded-lg text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${theme === 'dark' ? 'bg-[#2a2b2d] border-gray-700 text-white placeholder-gray-500' : 'bg-gray-50 border-gray-300 text-gray-900'}`}
                  />
                </div>
              </>
            ) : (withdrawfield as unknown as Record<string, unknown>).withdrawMethod === 'mobile_money' ? (
              <>
                <div className="mb-4">
                  <label className={`block text-sm font-semibold mb-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                    Network
                  </label>
                  <input
                    type="text"
                    onChange={(e)=> handleWithdraw(e.target.value, e.target.name)}
                    name='mobileNetwork'
                    value={String((withdrawfield as unknown as Record<string, unknown>).mobileNetwork || "")}
                    placeholder="Enter network (e.g. MTN, Airtel)"
                    className={`w-full px-3.5 py-2.5 border rounded-lg text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${theme === 'dark' ? 'bg-[#2a2b2d] border-gray-700 text-white placeholder-gray-500' : 'bg-gray-50 border-gray-300 text-gray-900'}`}
                  />
                </div>
                <div className="mb-4">
                  <label className={`block text-sm font-semibold mb-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                    Mobile Number
                  </label>
                  <input
                    type="text"
                    onChange={(e)=> handleWithdraw(e.target.value, e.target.name)}
                    name='mobileNumber'
                    value={String((withdrawfield as unknown as Record<string, unknown>).mobileNumber || "")}
                    placeholder="Enter mobile number"
                    className={`w-full px-3.5 py-2.5 border rounded-lg text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${theme === 'dark' ? 'bg-[#2a2b2d] border-gray-700 text-white placeholder-gray-500' : 'bg-gray-50 border-gray-300 text-gray-900'}`}
                  />
                </div>
                <div className="mb-6">
                  <label className={`block text-sm font-semibold mb-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                    Account Name
                  </label>
                  <input
                    type="text"
                    onChange={(e)=> handleWithdraw(e.target.value, e.target.name)}
                    name='accountname'
                    value={String((withdrawfield as unknown as Record<string, unknown>).accountname || "")}
                    placeholder="Enter account name"
                    className={`w-full px-3.5 py-2.5 border rounded-lg text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${theme === 'dark' ? 'bg-[#2a2b2d] border-gray-700 text-white placeholder-gray-500' : 'bg-gray-50 border-gray-300 text-gray-900'}`}
                  />
                </div>
              </>
            ) : (
              <div className="mb-6">
                <label className={`block text-sm font-semibold mb-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                  {t('enduser.wallet.withdrawfunds.withdrawToTitle', { defaultValue: 'Wallet Address' })}
                </label>
                <input
                  type="text"
                  onChange={(e)=> handleWithdraw(e.target.value, e.target.name)}
                  name='cryptoWalletAddress'
                  value={String((withdrawfield as unknown as Record<string, unknown>).cryptoWalletAddress || "")}
                  placeholder={t('enduser.wallet.withdrawfunds.cryptoPlaceholder', { defaultValue: 'Wallet address' })}
                  className={`w-full px-3.5 py-2.5 border rounded-lg text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${theme === 'dark' ? 'bg-[#2a2b2d] border-gray-700 text-white placeholder-gray-500' : 'bg-gray-50 border-gray-300 text-gray-900'}`}
                />
              </div>
            )}

           <button
  onClick={addWithdraw}
  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2.5 px-4 rounded-lg transition-colors text-sm shadow-sm"
>
  {t('enduser.wallet.withdrawfunds.button', { defaultValue: 'Withdraw Funds' })}
</button>

          </div>
        </div>

        {/* Transaction History */}
        <div className={`rounded-2xl shadow-lg border-2 ${theme === 'dark' ? 'bg-[#333436] border-gray-800' : 'bg-white border-gray-300'}`}>
          <div className="p-4 sm:p-8 border-b-2 border-gray-200">
            <h3 className={`text-lg font-bold mb-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
              {t('enduser.wallet.transactions.headertitle', { defaultValue: 'Transaction History' })}
            </h3>
            <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
              {t('enduser.wallet.transactions.headerbody', { defaultValue: 'Your payment and subscription transactions' })}
            </p>
          </div>

          {/* Tabs */}
          <div className={`flex gap-4 sm:gap-8 px-4 sm:px-8 pt-6 overflow-x-auto no-scrollbar border-b-2 ${theme === 'dark' ? 'border-gray-700' : 'border-gray-300'}`}>
            {transactionTabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`pb-4 text-base font-bold whitespace-nowrap transition-colors relative ${
                  activeTab === tab.key
                    ? theme === 'dark'
                      ? 'text-white'
                      : 'text-gray-900'
                    : theme === 'dark'
                    ? 'text-gray-400'
                    : 'text-gray-500'
                }`}
              >
                {tab.label}
                {activeTab === tab.key && (
                  <div className="absolute bottom-0 left-0 right-0 h-1 dark:bg-blue-600 bg-blue-600 rounded-t" />
                )}
              </button>
            ))}
          </div>

          {/* Transaction List */}
          <div className="p-4 sm:p-8">
            <div className="space-y-4 sm:space-y-5">
              {isLoadingTransactions ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                </div>
              ) : null}

              {!isLoadingTransactions && transactions?.map((transaction) => (
                <div
                  key={String((transaction as unknown as Record<string, unknown>).id)}
                  className={`flex flex-col sm:flex-row sm:items-center justify-between gap-4 sm:gap-0 p-4 sm:p-6 rounded-xl border-2 ${theme === 'dark' ? 'bg-[#2a2b2d] border-gray-700' : 'bg-gray-50 border-gray-200'}`}
                >
                  <div className="flex items-start sm:items-center gap-4 sm:gap-5 w-full sm:w-auto">
                    <div className={`w-10 h-10 sm:w-12 sm:h-12 flex-shrink-0 rounded-xl flex items-center justify-center ${theme === 'dark' ? 'bg-[#333436]' : 'bg-white'}`}>
                      <div className={`w-6 h-6 sm:w-8 sm:h-8 rounded-full flex items-center justify-center ${txStatusStyles(String((transaction as unknown as Record<string, unknown>).status)).dotBg}`}>
                        <div className={`w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full ${txStatusStyles(String((transaction as unknown as Record<string, unknown>).status)).dot}`} />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className={`text-base font-bold mb-1 truncate ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                        {String((transaction as unknown as Record<string, unknown>).type)}
                      </div>
                      <div className={`text-xs sm:text-sm font-medium truncate ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                        {(transaction as unknown as Record<string, unknown>).createdAt ? new Date(String((transaction as unknown as Record<string, unknown>).createdAt)).toLocaleString() : '' } • {String((transaction as unknown as Record<string, unknown>).paymentMethod)}
                      </div>
                    </div>
                  </div>
                  <div className={`flex items-center justify-between sm:justify-end w-full sm:w-auto gap-4 mt-2 sm:mt-0 pt-3 sm:pt-0 border-t sm:border-t-0 ${theme === 'dark' ? 'border-gray-800' : 'border-gray-100'}`}>
                    <div className={`text-base font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      {String((transaction as unknown as Record<string, unknown>).amount)}
                    </div>
                    <span className={`text-xs sm:text-sm font-bold px-3 py-1.5 sm:px-4 sm:py-2 rounded-full ${txStatusStyles(String((transaction as unknown as Record<string, unknown>).status)).badge}`}>
                      {txStatusLabel(String((transaction as unknown as Record<string, unknown>).status))}
                    </span>
                  </div>
                </div>
              ))}
              {!isLoadingTransactions && transactions.length === 0 && <p className='text-center'>{t('enduser.wallet.transactions.noHistory', { defaultValue: 'No Transaction History' })}</p>}

              {!isLoadingTransactions && transactionsPagination.hasMore ? (
                <div className="flex justify-center pt-2">
                  <button
                    onClick={loadMoreTransactions}
                    disabled={isLoadingMoreTransactions}
                    className={`px-6 py-3 rounded-lg font-semibold transition-colors border ${
                      theme === 'dark'
                        ? 'bg-[#2a2b2d] border-gray-700 text-white hover:bg-[#212123]'
                        : 'bg-white border-gray-300 text-gray-900 hover:bg-gray-100'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    {isLoadingMoreTransactions
                      ? t('enduser.wallet.transactions.loadingMore', { defaultValue: 'Loading...' })
                      : t('enduser.wallet.transactions.loadMore', { defaultValue: 'Load More' })}
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
 {showSuccess && (
          <div className="fixed top-6 right-6 z-[9999] animate-slide-in">
            <div className="bg-green-500 text-white px-6 py-4 rounded-xl shadow-2xl flex items-center gap-3">
              <CheckCircle className="w-6 h-6" />
              <div>
                <p className="font-semibold">{t('enduser.wallet.messages.successTitle', { defaultValue: 'Success!' })}</p>
                <p className="text-sm text-green-100">{t('enduser.wallet.messages.successBody', { defaultValue: 'Your purchase have been made successfully' })}</p>
              </div>
            </div>
          </div>
        )}
 {error && (<div className="fixed top-6 right-6 z-[9999] animate-slide-in">
             <div className="bg-red-500 text-white px-6 py-4 rounded-xl shadow-2xl flex items-center gap-3">
               <X className="w-6 h-6" />
               <div>
                 <p className="font-semibold">{t('enduser.wallet.messages.failedTitle', { defaultValue: 'Failed!' })}</p>
                 <p className="text-sm text-red-100">{error}</p>
               </div>
             </div>
           </div>)}
    <KxtPaymentMethodModal
      isOpen={showKxtPaymentMethodModal}
      onClose={() => setShowKxtPaymentMethodModal(false)}
      onProceed={async (method) => {
        setShowKxtPaymentMethodModal(false);
        if (method === 'wallet') {
          setShowKxtWalletBalanceModal(true);
        } else if (method === 'card') {
          setFlutterwaveMode('kxt');
          setShowFlutterwaveModal(true);
        } else if (method === 'crypto') {
          try {
            setIsBuyingKxt(true);
            const response = await buyKxtWithCrypto({ amountUsd: Number(kxtAmount) });
            if ((((response as unknown as Record<string, unknown>)?.data as Record<string, unknown>)?.paymentLink) || (response as unknown as Record<string, unknown>)?.paymentLink) {
              const link = (((response as unknown as Record<string, unknown>).data as Record<string, unknown>)?.paymentLink || (response as unknown as Record<string, unknown>).paymentLink) as string;
              window.location.href = link;
            } else {
              throw new Error("No payment link returned");
            }
          } catch (err: unknown) {
            setError(getErrorMessage(err) || 'Failed to initialize crypto payment');
            setTimeout(() => setError(""), 3000);
            setIsBuyingKxt(false);
          }
        }
      }}
    />

    <WalletBalanceModal
      isOpen={showKxtWalletBalanceModal}
      onClose={() => setShowKxtWalletBalanceModal(false)}
      selectedSource={selectedKxtWalletBalance}
      onSelectSource={setSelectedKxtWalletBalance}
      balances={balance}
      amountUsd={kxtAmount}
      isProcessing={isBuyingKxt}
      t={t}
      onProceed={async () => {
        try {
          setIsBuyingKxt(true);
          await buyKxtWithBalance({ amountUsd: Number(kxtAmount), walletSource: selectedKxtWalletBalance });
          setShowKxtWalletBalanceModal(false);
          setShowSuccess(true);
          setTimeout(() => setShowSuccess(false), 3000);
          refreshWalletData();
        } catch (err: unknown) {
          setError(getErrorMessage(err) || 'Failed to buy KXT with balance');
          setTimeout(() => setError(""), 3000);
        } finally {
          setIsBuyingKxt(false);
        }
      }}
    />

    <CryptoFundModal
      isOpen={showCryptoFundModal}
      onClose={closeCryptoFundModal}
      onProceed={proceedCryptoTopup}
      amount={flutterwaveMode === 'kxt' ? String(kxtAmount) : String((fundfield as unknown as Record<string, unknown>).amount)}
      cryptoDetails={cryptoDetails}
      onCryptoDetailsChange={handleCryptoDetailsChange}
      isProcessing={isInitializingCryptoTopup}
    />

    <CryptoPaymentModal
      isOpen={showCryptoPaymentModal}
      onClose={closeCryptoPaymentModal}
      onPaid={handleCryptoPaymentVerify}
      isVerifying={isVerifyingCryptoTopup}
      data={cryptoPaymentData}
    />
    <FlutterwaveUserInfoModal
      isOpen={showFlutterwaveModal}
      onClose={() => {
        setShowFlutterwaveModal(false);
        setFlutterwaveMode('fund');
      }}
      userInfo={userInfo}
      setUserInfo={setUserInfo}
      onProceed={async () => {
        const isFormValid = userInfo.email.trim() && userInfo.phone.trim() && userInfo.fullName.trim();
        if (!isFormValid) {
          setError(t('enduser.wallet.messages.fillRequired', { defaultValue: 'Please fill in all required fields.' }));
          setTimeout(() => setError(""), 3000);
          return;
        }

        const isKxtPurchase = flutterwaveMode === 'kxt';
        const sourceAmount = isKxtPurchase ? Number(kxtAmount) : Number((fundfield as unknown as Record<string, unknown>).amount);

        let flutterwaveCurrency = (import.meta.env.VITE_FLUTTERWAVE_CURRENCY as string | undefined) || 'USD';
        let paymentAmount = sourceAmount;
        const originalUsdAmount = sourceAmount;
        
        if (!isKxtPurchase && paymentMethod === 'mobile_money') {
          try {
            const countryResponse = await apiClient.get(ENDPOINTS.USER.COUNTRY);
            if (countryResponse.data?.success && countryResponse.data?.data?.currency) {
              const userCurrency = countryResponse.data.data.currency;

              if (userCurrency !== 'USD') {
                // Convert USD amount to local currency using open.er-api.com (supports African currencies)
                try {
                  const rateResponse = await fetch(
                    `https://open.er-api.com/v6/latest/USD`
                  );
                  const rateData = await rateResponse.json();
                  if (rateData?.result === 'success' && rateData?.rates?.[userCurrency]) {
                    const rate = rateData.rates[userCurrency];
                    paymentAmount = Math.ceil(paymentAmount * rate);
                    flutterwaveCurrency = userCurrency;
                  } else {
                    console.error(`Exchange rate not found for ${userCurrency}, keeping USD.`);
                  }
                } catch (conversionErr) {
                  console.error("Currency conversion failed, falling back to USD.", conversionErr);
                }
              }
            }
          } catch (err: unknown) {
            console.error("Failed to fetch user country/currency. Falling back to default.");
          }
        }

        try {
          localStorage.setItem('pendingWalletTopup', 'true');
          localStorage.setItem('pendingWalletTopupAmount', String(originalUsdAmount));
          localStorage.setItem('pendingWalletTopupCurrency', flutterwaveCurrency);
          const response = await initializeFlutterwave({
            amount: paymentAmount,
            currency: flutterwaveCurrency,
            phone: userInfo.phone,
            meta: {
              order_type: 'wallet_topup',
              topup_amount: originalUsdAmount,
              ...(isKxtPurchase
                ? { wallet_type: 'kxt', currency: 'USD' }
                : { currency: 'USD', payment_method: paymentMethod }
              )
            }
          });
          if (!response?.success || !response.data?.paymentLink) {
            throw new Error(response?.message || 'Failed to initialize payment');
          }
          setFlutterwavePaymentLink(response.data.paymentLink);
          setShowFlutterwaveModal(false);
          setFlutterwaveMode('fund');
          setShowFlutterwaveIframe(true);
        } catch (err: unknown) {
          setError(getErrorMessage(err) || 'Failed to initialize Flutterwave payment');
          setTimeout(() => setError(""), 3000);
        }
      }}
      amount={flutterwaveMode === 'kxt' ? String(kxtAmount) : String((fundfield as unknown as Record<string, unknown>).amount)}
    />

    <FlutterwaveIframeModal
      isOpen={showFlutterwaveIframe}
      paymentLink={flutterwavePaymentLink}
      onClose={() => {
        setShowFlutterwaveIframe(false);
        setFlutterwavePaymentLink(null);
        const topupSuccess = localStorage.getItem('walletTopupSuccess') === 'true';
        if (topupSuccess) {
          localStorage.removeItem('walletTopupSuccess');
          setShowSuccess(true);
          setTimeout(() => setShowSuccess(false), 3000);
        }
      }}
    />
    <FeedbackModal
      isOpen={showFeedback}
      onClose={() => setShowFeedback(false)}
      type={feedbackData.type}
      title={feedbackData.title}
      message={feedbackData.message}
      autoClose={feedbackData.type === 'success'}
      autoCloseDuration={4000}
    />
    </>
  );
}

interface CryptoFundModalProps {
  isOpen: boolean;
  onClose: () => void;
  onProceed: () => void;
  amount: string;
  cryptoDetails: CryptoPaymentDetails;
  onCryptoDetailsChange: (field: keyof CryptoPaymentDetails, value: string) => void;
  isProcessing: boolean;
}

function CryptoFundModal({
  isOpen,
  onClose,
  onProceed,
  amount,
  cryptoDetails,
  onCryptoDetailsChange,
  isProcessing
}: CryptoFundModalProps) {
  const { t } = useTranslation();
  if (!isOpen) return null;

  const isFormValid = Boolean(
    Number(amount) > 0 &&
      cryptoDetails.crypto &&
      cryptoDetails.chain &&
      cryptoDetails.baseFiat
  );

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-[#2a2b2e] w-[92vw] max-w-[600px] max-h-[85vh] rounded-2xl shadow-2xl overflow-y-auto">
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700 sticky top-0 bg-white dark:bg-[#2a2b2e] z-10">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
            Crypto Add Funds
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            aria-label="Close modal"
          >
            <X className="w-5 h-5 sm:w-6 sm:h-6 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        <div className="px-4 sm:px-6 py-4 bg-blue-50 dark:bg-blue-900/20 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">{t('enduser.wallet.payment.amountLabel', { defaultValue: 'Amount' })}</p>
              <p className="text-xl sm:text-2xl font-bold text-blue-600">
                {Number(amount || 0).toLocaleString()} {cryptoDetails.baseFiat}
              </p>
            </div>
          </div>
        </div>

        <div className="p-4 sm:p-6 space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Coin
              </label>
              <select
                value={cryptoDetails.crypto}
                onChange={(e) => onCryptoDetailsChange('crypto', e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="USDT">USDT</option>
                <option value="USDC">USDC</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Network
              </label>
              <select
                value={cryptoDetails.chain}
                onChange={(e) => onCryptoDetailsChange('chain', e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="BSC_TESTNET">BSC Testnet</option>
                <option value="SOLANA_DEVNET">SOLANA DEVNET</option>
                {/* <option value="POLYGON">Polygon</option> */}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Amount
            </label>
            <input
              type="text"
              value={`${Number(amount || 0).toLocaleString()} ${cryptoDetails.baseFiat}`}
              readOnly
              className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 p-4 sm:p-6 border-t border-gray-200 dark:border-gray-700 sticky bottom-0 bg-white dark:bg-[#2a2b2e]">
          <button
            onClick={onClose}
            disabled={isProcessing}
            className="px-6 py-2.5 text-gray-700 dark:text-gray-300 font-medium hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors order-2 sm:order-1 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={onProceed}
            disabled={!isFormValid || isProcessing}
            className="px-8 py-2.5 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 order-1 sm:order-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isProcessing ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>{t('enduser.wallet.payment.preparing', { defaultValue: 'Preparing payment...' })}</span>
              </>
            ) : (
              <span>{t('enduser.wallet.payment.continue', { defaultValue: 'Continue' })}</span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

function CryptoPaymentModal({
  isOpen,
  onClose,
  onPaid,
  isVerifying,
  data
}: { isOpen: boolean; onClose: () => void; onPaid: () => void; isVerifying: boolean; data: unknown }) {
  return (
    <DonationCryptoPaymentModal
      isOpen={isOpen}
      onClose={onClose}
      onPaid={onPaid}
      isVerifying={isVerifying}
      data={
        data
          ? {
              ...(data as Record<string, unknown>),
              message: "Send payment to the address below to complete your wallet top-up.",
            }
          : null
      }
    />
  );
}

interface FlutterwaveUserInfoModalProps {
  isOpen: boolean;
  onClose: () => void;
  userInfo: {
    email: string;
    phone: string;
    fullName: string;
  };
  setUserInfo: React.Dispatch<React.SetStateAction<{
    email: string;
    phone: string;
    fullName: string;
  }>>;
  onProceed: () => void;
  amount: string;
}

function FlutterwaveUserInfoModal({
  isOpen,
  onClose,
  userInfo,
  setUserInfo,
  onProceed,
  amount
}: FlutterwaveUserInfoModalProps) {
  const { t } = useTranslation();
  if (!isOpen) return null;

  const isFormValid = Boolean(
    userInfo.email.trim() &&
    userInfo.phone.trim() &&
    userInfo.fullName.trim()
  );

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-[#2a2b2e] w-[92vw] max-w-[600px] max-h-[85vh] rounded-2xl shadow-2xl overflow-y-auto">
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700 sticky top-0 bg-white dark:bg-[#2a2b2e] z-10">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
            Add Funds
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            aria-label="Close modal"
          >
            <X className="w-5 h-5 sm:w-6 sm:h-6 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        <div className="px-4 sm:px-6 py-4 bg-blue-50 dark:bg-blue-900/20 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">{t('enduser.wallet.payment.amountLabel', { defaultValue: 'Amount' })}</p>
              <p className="text-xl sm:text-2xl font-bold text-blue-600">
                ${Number(amount || 0).toFixed(2)}
              </p>
            </div>
          </div>
        </div>

        <div className="p-4 sm:p-6">
          <form className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Email Address
              </label>
              <input
                type="email"
                placeholder="your@email.com"
                value={userInfo.email}
                onChange={(e) => setUserInfo({ ...userInfo, email: e.target.value })}
                className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Phone Number
              </label>
              <input
                type="tel"
                placeholder="+234 xxx xxx xxxx"
                value={userInfo.phone}
                onChange={(e) => setUserInfo({ ...userInfo, phone: e.target.value })}
                className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Full Name
              </label>
              <input
                type="text"
                placeholder="John Doe"
                value={userInfo.fullName}
                onChange={(e) => setUserInfo({ ...userInfo, fullName: e.target.value })}
                className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
          </form>
        </div>

        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 p-4 sm:p-6 border-t border-gray-200 dark:border-gray-700 sticky bottom-0 bg-white dark:bg-[#2a2b2e]">
          <button
            onClick={onClose}
            className="px-6 py-2.5 text-gray-700 dark:text-gray-300 font-medium hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors order-2 sm:order-1"
          >
            Cancel
          </button>
          <button
            onClick={onProceed}
            disabled={!isFormValid}
            className="px-8 py-2.5 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 order-1 sm:order-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span>{t('enduser.plan.actions.proceed', { defaultValue: 'Proceed to Payment' })}</span>
          </button>
        </div>
      </div>
    </div>
  );
}

interface FlutterwaveIframeModalProps {
  isOpen: boolean;
  paymentLink: string | null;
  onClose: () => void;
}

function FlutterwaveIframeModal({
  isOpen,
  paymentLink,
  onClose
}: FlutterwaveIframeModalProps) {
  const { t } = useTranslation();
  const isLocalhost =
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1' ||
      window.location.hostname === '[::1]');

  useEffect(() => {
    if (!isOpen || !paymentLink || !isLocalhost) return;
    window.location.assign(paymentLink);
  }, [isOpen, paymentLink, isLocalhost]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-neutral-200/90 flex items-center justify-center z-50 p-4">
      <div className="bg-[#f2f2f2] w-[92vw] max-w-[420px] max-h-[90vh] rounded-2xl shadow-2xl overflow-hidden flex flex-col border border-neutral-200">
        <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-200 bg-[#f7f7f7]">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-neutral-900 flex items-center justify-center">
              <span className="text-white text-sm font-semibold">KX</span>
            </div>
            <div>
              <p className="text-sm font-semibold text-neutral-900">KonectX</p>
              <p className="text-xs text-neutral-500">{t('enduser.plan.payment.secureCheckout', { defaultValue: 'Secure Checkout' })}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-neutral-200 rounded-full transition-colors"
            aria-label="Close modal"
          >
            <X className="w-4 h-4 text-neutral-500" />
          </button>
        </div>

        <div className="flex-1 bg-[#f2f2f2]">
          {paymentLink ? (
            isLocalhost ? (
              <div className="h-[70vh] flex items-center justify-center p-6 text-center">
                <div className="space-y-3">
                  <p className="text-sm text-neutral-700">
                    Localhost detected. Opening payment page in your browser.
                  </p>
                  <button
                    onClick={() => window.location.assign(paymentLink)}
                    className="px-4 py-2 rounded-lg bg-neutral-900 text-white text-sm hover:bg-neutral-800 transition-colors"
                  >
                    Open Payment Page
                  </button>
                </div>
              </div>
            ) : (
              <iframe
               
                src={paymentLink}
                className="w-full h-[70vh] border-0 bg-[#f2f2f2]"
                allow="payment"
              />
            )
          ) : (
            <div className="h-[70vh] flex items-center justify-center">
              <p className="text-sm text-neutral-600">{t('enduser.plan.payment.loadingLink', { defaultValue: 'Loading payment link...' })}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

interface KxtPaymentMethodModalProps {
  isOpen: boolean;
  onClose: () => void;
  onProceed: (method: 'wallet' | 'card' | 'crypto') => void;
}

function KxtPaymentMethodModal({
  isOpen,
  onClose,
  onProceed
}: KxtPaymentMethodModalProps) {
  const { t } = useTranslation();
  const [selectedMethod, setSelectedMethod] = useState<'wallet' | 'card' | 'crypto' | null>(null);

  if (!isOpen) return null;

  const paymentMethods = [
    {
      id: 'wallet' as const,
      name: 'Wallet',
      description: 'Pay using your wallet balance',
      icon: Wallet
    },
    {
      id: 'card' as const,
      name: 'Credit, Debit Card or Mobile Money',
      description: 'Pay with debit, credit card or mobile money',
      icon: CreditCard
    },
    {
      id: 'crypto' as const,
      name: 'Crypto Payment',
      description: 'Pay with USDT/USDC',
      icon: function CryptoIcon(props: Record<string, unknown>) {
        return (
          <svg viewBox="0 0 32 32" aria-hidden="true" {...props}>
            <circle cx="16" cy="16" r="15" fill="currentColor" />
            <text x="16" y="16" textAnchor="middle" dominantBaseline="central" fontSize="12" fill="#ffffff" fontWeight="700">C</text>
          </svg>
        );
      }
    }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-[#2a2b2e] rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700 sticky top-0 bg-white dark:bg-[#2a2b2e] z-10">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
            Choose Payment Option
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            aria-label="Close modal"
          >
            <X className="w-5 h-5 sm:w-6 sm:h-6 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        {/* Payment Methods */}
        <div className="p-4 sm:p-6 space-y-3">
          {paymentMethods.map((method) => {
            const Icon = method.icon;
            const isSelected = selectedMethod === method.id;
            
            return (
              <button
                key={method.id}
                onClick={() => setSelectedMethod(method.id)}
                className={`w-full p-3 sm:p-4 rounded-xl border-2 transition-all text-left ${
                  isSelected
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                <div className="flex items-start gap-3 sm:gap-4">
                  <div className={`p-2 sm:p-3 rounded-lg flex-shrink-0 ${
                    isSelected
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
                  }`}>
                    <Icon className="w-5 h-5 sm:w-6 sm:h-6" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <h3 className="font-semibold text-sm sm:text-base text-gray-900 dark:text-white truncate">
                        {method.name}
                      </h3>
                      {isSelected && (
                        <div className="w-5 h-5 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0">
                          <Check className="w-3 h-3 text-white" />
                        </div>
                      )}
                    </div>
                    <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-1">
                      {method.description}
                    </p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Footer */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 p-4 sm:p-6 border-t border-gray-200 dark:border-gray-700 sticky bottom-0 bg-white dark:bg-[#2a2b2e]">
          <button
            onClick={onClose}
            className="px-6 py-2.5 text-gray-700 dark:text-gray-300 font-medium hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors order-2 sm:order-1"
          >
            Cancel
          </button>
          <button
            onClick={() => selectedMethod && onProceed(selectedMethod)}
            disabled={!selectedMethod}
            className="px-8 py-2.5 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 order-1 sm:order-2"
          >
            <span className="text-sm sm:text-base">{t('enduser.plan.actions.proceed', { defaultValue: 'Proceed to Payment' })}</span>
          </button>
        </div>
      </div>
    </div>
  );
}

